
import { HelpContent } from './HelpPopover';

export const HELP_CONTENT: Record<string, HelpContent> = {
    GIFTS: {
        what_it_means: "A gift usually means money, property, or valuables given away during someone’s lifetime, not including normal living expenses or small presents.",
        why_we_ask: "Large gifts made in the last 7 years can affect which inheritance tax form is needed, even if no tax is due.",
        how_precise: "You don’t need exact amounts right now. A yes/no or rough idea is enough to continue.",
        examples: [
            "Helping someone with a house deposit",
            "A large cash gift",
            "Giving away a car or valuable item",
            "Transferring property ownership"
        ]
    },
    SOLE_OWNERSHIP: {
        what_it_means: "Sole ownership means the asset was in their name only, not shared with another person.",
        why_we_ask: "This helps determine whether any formal probate paperwork is needed to access or transfer the asset.",
        how_precise: "If you’re unsure, choose ‘Not sure yet’. We can still continue.",
        examples: [
            "A house in their name only",
            "A bank account only they could access",
            "Shares held in their name only"
        ]
    },
    TRUSTS: {
        what_it_means: "A trust or life interest is a legal arrangement where someone can benefit from an asset without fully owning it.",
        why_we_ask: "These arrangements can change which reporting route applies, even if no tax is payable.",
        how_precise: "You don’t need legal documents at this stage — just your best understanding.",
        examples: [
            "A will saying someone can live in the home for life",
            "Money held ‘in trust’ for children",
            "Anything described as a ‘life interest’"
        ]
    },
    FOREIGN_ASSETS: {
        what_it_means: "Foreign assets are things owned outside the UK, such as property or bank accounts abroad.",
        why_we_ask: "Assets outside the UK can affect how the estate needs to be reported.",
        how_precise: "A simple yes/no/not sure is enough for now.",
        examples: [
            "A holiday home abroad",
            "A bank account in another country",
            "Overseas investments"
        ]
    },
    NET_VALUE: {
        what_it_means: "Net value is the total value of assets minus any debts or money owed.",
        why_we_ask: "This helps estimate whether any inheritance tax might apply.",
        how_precise: "A rough estimate is fine. You can update this later.",
        examples: [
            "Property about £400,000",
            "Savings around £50,000",
            "Mortgage roughly £120,000"
        ]
    },
    PENSIONS_IN_ESTATE: {
        what_it_means: "Most pensions have a 'nominated beneficiary' (like a spouse) so they pay out directly and tax-free. If not, the money enters the estate.",
        why_we_ask: "Money paid into the estate adds to the total taxable value.",
        how_precise: "If unsure, we assume it is excluded (tax-free) for now.",
        examples: [
            "A workplace pension paying to a named person",
            "A private pension provider contacting beneficiaries",
            "If unsure, it’s common to select ‘Not sure’"
        ]
    },
    MAIN_HOME: {
        what_it_means: "The main residence is the property where the deceased lived most of the time.",
        why_we_ask: "Identifying the main home allows us to apply the Residence Nil Rate Band (RNRB), an extra tax-free allowance of up to £175,000.",
        how_precise: "Select the property that was their primary home.",
        examples: [
            "The family home they lived in",
            "A property they moved out of (if 'Downsizing' rules apply)",
            "Not a buy-to-let or holiday cottage"
        ]
    },
    DECEASED_HOME_SHARE_PERCENT: {
        what_it_means: "This is the part of the home that legally belonged to the deceased at death.",
        why_we_ask: "The extra home allowance (RNRB) only applies to the part of the property that passes to children or grandchildren.",
        examples: [
            "Deceased owned 50% of the home and left that 50% to children",
            "Deceased owned 50% and left it to their spouse (no RNRB for that share)",
            "Deceased owned 100% and left it to children"
        ]
    },
    DECEASED_HOME_SHARE_PASSES_TO: {
        what_it_means: "Who inherits the deceased's specific share of this property?",
        why_we_ask: "To get the extra RNRB allowance (up to £175,000), the home must pass to direct descendants. Direct descendants include children, grandchildren, stepchildren and adopted children.",
        examples: [
            "Direct Descendants (qualifies for RNRB)",
            "Spouse / Civil Partner (exempt from IHT)",
            "Charity (exempt from IHT)",
            "Other Beneficiary (fully taxable, no RNRB)"
        ]
    },
    DESCENDANT_INHERITANCE_TYPE: {
        what_it_means: "How the descendants inherit the home — outright, or through a trust, and if so what kind.",
        why_we_ask: "The residence nil-rate band (up to £175,000) is only available where the home is 'closely inherited'. Inheriting outright, via a bare/absolute trust, or a life-interest trust (IPDI) for the descendant qualifies. A trust until the children reach 18 or 25 qualifies only if it was set up by a parent's will or under the intestacy rules — not, for example, a grandparent's will trust for grandchildren. A discretionary trust does not qualify — no individual is treated as inheriting, so the RNRB is generally lost.",
        examples: [
            "Left to the children outright in the will (qualifies)",
            "Held on a discretionary trust for the family (does NOT qualify — RNRB lost)",
            "An appointment out of a discretionary trust within 2 years of death can restore the RNRB"
        ]
    },
    BEREAVED_TRUST_ORIGIN: {
        what_it_means: "Who set up the trust that holds the home until the children reach 18 or 25.",
        why_we_ask: "A trust like this counts as a bereaved-minor or 18-to-25 trust, so the residence nil-rate band can apply, only if it was set up by the will of a parent of the children, or arises under the intestacy rules of England and Wales because there is no will. A parent includes a step-parent, or someone who had parental responsibility for the child immediately before they died. The same kind of trust in a grandparent's will does not qualify, unless the grandparent had parental responsibility.",
        examples: [
            "A parent's will leaves the home on trust for their children until 25 (qualifies)",
            "A grandparent's will leaves the home on trust for the grandchildren until 25 (does NOT qualify)",
            "A grandparent's will gives a grandchild the right to the income now (choose 'Life-interest trust (IPDI)' instead)"
        ]
    },
    TRANSFERABLE_ALLOWANCE: {
        what_it_means: "If a spouse/civil partner died before the deceased, any unused allowance from the first death can sometimes be transferred.",
        why_we_ask: "This can increase the tax-free amount available on the second death.",
        examples: [
            "If 100% unused NRB is available, the NRB could double (e.g. £325k → £650k).",
            "If you’re not sure, you can leave it as 0 for now and update later."
        ]
    },
    NRB: {
        what_it_means: "The Nil Rate Band is the basic tax-free allowance for inheritance tax (currently £325,000 in many cases).",
        why_we_ask: "It helps estimate whether any inheritance tax might be due.",
        how_precise: "Enter the percentage unused (0-100%).",
        examples: [
            "If the net estate is £300,000 and there are no other complications, tax may be £0.",
            "If the net estate is £600,000, the amount above the allowance may be taxable."
        ]
    },
    RNRB: {
        what_it_means: "The Residence Nil Rate Band is an extra allowance that can apply when a main home (or the deceased’s share of it) is left to qualifying beneficiaries.",
        why_we_ask: "It can reduce inheritance tax by up to an additional allowance (often up to £175,000, subject to rules and tapering).",
        how_precise: "Enter the percentage unused (0-100%).",
        examples: [
            "If the deceased owned 50% of a £650,000 home and that 50% goes to qualifying inheritors, the ‘closely inherited’ value is £325,000.",
            "If the deceased’s share goes to a spouse instead, RNRB usually won’t apply at this stage."
        ]
    },
    UNUSED_NRB_PERCENT: {
        what_it_means: "This is the percentage of the first spouse’s Nil Rate Band that was unused and can be claimed now (0–100%).",
        why_we_ask: "It affects the total tax-free allowance.",
        examples: ["0%: they used it all", "100%: they used none of it"]
    },
    UNUSED_RNRB_PERCENT: {
        what_it_means: "This is the percentage of the first spouse’s Residence Nil Rate Band that was unused and can be claimed now.",
        why_we_ask: "It can increase the total RNRB available for this estate.",
        examples: ["0%: they used it all", "100%: they didn't own a home or left it to spouse"]
    }
};
